import { CircularProgress } from '@mui/material';
import Typography from '@mui/material/Typography';
import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'shared/assets/icons';
import { Locales } from 'shared/assets/localization';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { FormSelect } from 'shared/components/ui/form-select';
import { IconButton } from 'shared/components/ui/icon-button';
import { version } from 'shared/constants';
import { NotificationsToggles } from 'shared/dialogs/user-settings/notifications-toggles';
import { PaymentManagement } from 'shared/dialogs/user-settings/payment-management';
import { useCachedPushNotificationTokenQuery } from 'shared/hooks/queries/use-cached-push-notification-token-query';
import { useDateFormatOptions } from 'shared/hooks/use-date-format-options';
import { useElectron } from 'shared/hooks/use-is-mac';
import { useNumberFormatOptions } from 'shared/hooks/use-number-format-options';
import { useSupportedLanguageOptions } from 'shared/hooks/use-supported-language-options';
import { useThemeModeOptions } from 'shared/hooks/use-theme-mode-options';
import { useTimeFormatOptions } from 'shared/hooks/use-time-format-options';
import { useUser } from 'shared/hooks/use-user';
import { useWeekDaysOptions } from 'shared/hooks/use-week-days-options';
import {
  DateFormatOptions,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import { NumberFormatOptions } from 'shared/types/number-format-options';
import { ThemeModes } from 'shared/types/theme-modes';
import { WeekDays } from 'shared/types/week-days';
import { isVersionGreater } from 'shared/utils/is-version-greater';

import {
  CreateTaskShortcut,
  LoadingCreateTaskShortcut,
} from './create-task-shortcut';
import { CreateTaskShortcutFallback } from './create-task-shortcut-fallback';
import { ExternalCalendarList } from './external-calendar-list';
import * as Styled from './settings-form.style';
import { UserName } from './user-name';

export type SettingsFormProps = {
  onClose: () => void;
  name: string;
  onChangeName: (name: string) => void;
  language?: Locales;
  onChangeLanguage: (language: Locales) => void;
  isSignedInWithProvider?: boolean;
  email: string;
  onChangePassword: () => void;
  numberFormat?: NumberFormatOptions;
  onChangeNumberFormat: (numberFormat: NumberFormatOptions) => void;
  dateFormat: DateFormatOptions;
  onChangeDateFormat: (dateFormat: DateFormatOptions) => void;
  timeFormat: TimeFormatOptions;
  onChangeTimeFormat: (dateFormat: TimeFormatOptions) => void;
  weekStartSetOnIos?: boolean;
  weekStartsOn: WeekDays;
  onChangeWeekStart: (weekDay: WeekDays) => void;
  themePreference: ThemeModes;
  onChangeThemePreference: (themeMode: ThemeModes) => void;
};

export const SettingsForm: React.FC<SettingsFormProps> = ({
  onClose,
  name,
  onChangeName,
  language,
  onChangeLanguage,
  isSignedInWithProvider,
  email,
  onChangePassword,
  numberFormat,
  onChangeNumberFormat,
  dateFormat,
  onChangeDateFormat,
  timeFormat,
  onChangeTimeFormat,
  weekStartSetOnIos,
  weekStartsOn,
  onChangeWeekStart,
  themePreference,
  onChangeThemePreference,
}) => {
  const { t } = useTranslation();
  const { version: electronVersion } = useElectron();
  const user = useUser();
  const supportedLanguageOptions = useSupportedLanguageOptions();
  const numberFormatOptions = useNumberFormatOptions();
  const dateFormatOptions = useDateFormatOptions();
  const timeFormatOptions = useTimeFormatOptions();
  const themeModeOptions = useThemeModeOptions();
  const weekDaysOptions = useWeekDaysOptions();
  const { data: pushNotificationToken } = useCachedPushNotificationTokenQuery(
    user?.uid,
  );

  const onChangeLang = (e: React.ChangeEvent<HTMLSelectElement>) =>
    onChangeLanguage(e.target.value as Locales);

  const onCopyPushToken = () => {
    if (pushNotificationToken) {
      navigator.clipboard.writeText(pushNotificationToken);
    }
  };

  const onChangeNumberFormatOption = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => onChangeNumberFormat(e.target.value as NumberFormatOptions);

  const onChangeDateFormatOption = (e: React.ChangeEvent<HTMLSelectElement>) =>
    onChangeDateFormat(e.target.value as DateFormatOptions);

  const onChangeTimeFormatOption = (e: React.ChangeEvent<HTMLSelectElement>) =>
    onChangeTimeFormat(e.target.value as TimeFormatOptions);

  const onChangeWeekStartsOnOption = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => onChangeWeekStart(e.target.value as WeekDays);

  const onChangeThemeMode = (e: React.ChangeEvent<HTMLSelectElement>) =>
    onChangeThemePreference(e.target.value as ThemeModes);

  return (
    <Styled.Container>
      <Styled.Header>
        <Typography variant="h5" component="h1">
          {t('forms.user-settings.title')}
        </Typography>
        <IconButton icon={X} onClick={onClose} />
      </Styled.Header>

      <Styled.Body>
        {/* name */}
        <Styled.Section>
          <Typography variant="h6" component="label" htmlFor="name">
            {t('forms.user-settings.sections.name.label')}
          </Typography>

          <UserName id="name" value={name} onChange={onChangeName} />
        </Styled.Section>

        {/* subscription */}
        <Suspense fallback={<CircularProgress size="3.2rem" color="info" />}>
          <PaymentManagement />
        </Suspense>

        {/* General */}
        <Styled.Section>
          <Typography variant="h6" component="span">
            {t('forms.user-settings.sections.general.label')}
          </Typography>
          <FormSelect
            id="language"
            options={supportedLanguageOptions}
            placeholder={t(
              'forms.user-settings.sections.language.fields.language.placeholder',
            )}
            label={t(
              'forms.user-settings.sections.language.fields.language.label',
            )}
            value={language}
            onChange={onChangeLang}
            fullWidth
          />
          <FormSelect
            id="thema"
            options={themeModeOptions}
            label={t(
              'forms.user-settings.sections.appearance.theme-preference.label',
            )}
            value={themePreference}
            onChange={onChangeThemeMode}
            fullWidth
          />
          <FormSelect
            id="number-format"
            options={numberFormatOptions}
            label={t('forms.user-settings.number-format.label')}
            value={numberFormat}
            onChange={onChangeNumberFormatOption}
            fullWidth
          />
          <FormSelect
            id="date-format"
            options={dateFormatOptions}
            label={t(
              'forms.user-settings.sections.date-and-time.date-format.label',
            )}
            value={dateFormat}
            onChange={onChangeDateFormatOption}
            fullWidth
          />

          <FormSelect
            id="time-format"
            options={timeFormatOptions}
            label={t(
              'forms.user-settings.sections.date-and-time.time-format.label',
            )}
            value={timeFormat}
            onChange={onChangeTimeFormatOption}
            fullWidth
          />
          <FormSelect
            id="week-start"
            options={weekDaysOptions}
            label={t(
              'forms.user-settings.sections.date-and-time.week-start.label',
            )}
            value={weekStartsOn}
            disabled={weekStartSetOnIos}
            onChange={onChangeWeekStartsOnOption}
            helperText={
              weekStartSetOnIos
                ? t(
                    'forms.user-settings.sections.date-and-time.week-start-set-on-ios.label',
                  )
                : undefined
            }
            fullWidth
          />
          {/* Create Task Shortcut */}
          {electronVersion && isVersionGreater('2.0.0', electronVersion) ? (
            <Suspense fallback={<LoadingCreateTaskShortcut />}>
              <CreateTaskShortcut />
            </Suspense>
          ) : (
            <CreateTaskShortcutFallback />
          )}
        </Styled.Section>

        {/* security */}
        <Styled.Section>
          <Typography variant="h6" component="span">
            {t('forms.user-settings.sections.security.label')}
          </Typography>

          {isSignedInWithProvider ? (
            <>
              <Styled.SubSection>
                <Typography variant="body2" component="p">
                  {t('forms.user-settings.sections.security.siwa.title')}
                </Typography>
                <Styled.SecondaryTextTypography variant="overline">
                  {email}
                </Styled.SecondaryTextTypography>
              </Styled.SubSection>
            </>
          ) : (
            <>
              <Styled.SubSection>
                <Typography variant="body2" component="p">
                  {t('forms.user-settings.sections.security.email.title')}
                </Typography>
                <Styled.SecondaryTextTypography variant="overline" $canCopy>
                  {email}
                </Styled.SecondaryTextTypography>
              </Styled.SubSection>
              <Styled.SplitSectionRow>
                <Typography variant="body2" component="span">
                  {t('forms.user-settings.sections.security.password.label')}
                </Typography>
                <Button
                  onClick={onChangePassword}
                  size={Sizes.Small}
                  variant={Variants.Outlined}
                  color={Colors.Inherit}
                >
                  {t('forms.user-settings.sections.security.password.button')}
                </Button>
              </Styled.SplitSectionRow>
            </>
          )}
        </Styled.Section>

        {/* calendar */}
        <Styled.Section>
          <Typography variant="h6" component="span">
            {t('forms.user-settings.sections.calendar.label')}
          </Typography>
          <Suspense fallback={<CircularProgress size="3.2rem" color="info" />}>
            <ExternalCalendarList />
          </Suspense>
        </Styled.Section>

        {/* notifications */}
        <Styled.Section>
          <Typography
            variant="h6"
            component="span"
            onDoubleClick={onCopyPushToken}
          >
            {t('forms.user-settings.sections.notifications.label')}
          </Typography>
          <NotificationsToggles />
        </Styled.Section>

        {/* delete account */}
        <Styled.Section>
          <Typography variant="h6" component="label">
            {t('forms.user-settings.sections.delete-account.label')}
          </Typography>
          <Styled.SecondaryTextTypography variant="overline">
            {t('forms.user-settings.sections.delete-account.description.1')}
            <Styled.Anchor href="mailto:support@griply.app">
              support@griply.app
            </Styled.Anchor>
            {t('forms.user-settings.sections.delete-account.description.2')}
          </Styled.SecondaryTextTypography>
        </Styled.Section>

        {/* app version */}
        <Styled.Section>
          <Styled.SecondaryTextTypography variant="overline" textAlign="center">
            {t('forms.user-settings.sections.app-version.label', {
              version,
            })}
          </Styled.SecondaryTextTypography>
        </Styled.Section>
      </Styled.Body>
    </Styled.Container>
  );
};
